import {
  DEADLINE_TAKE_OVER_MAX_DAYS,
  DEFAULT_DEADLINE_WARNING_DAYS,
  type DeadlineBucket,
  type DeadlineManagedByValue,
  type RecurrenceAnchorValue,
} from '../dto/deadline/deadline.constants';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function parseIsoDay(isoDay: string): { year: number; month: number; day: number } {
  const match = ISO_DAY.exec(isoDay);
  if (!match) {
    throw new Error(`Expected YYYY-MM-DD, got "${isoDay}"`);
  }
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function toUtcMs(isoDay: string): number {
  const { year, month, day } = parseIsoDay(isoDay);
  return Date.UTC(year, month - 1, day);
}

function fromUtcDate(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export function addDaysToIsoDay(isoDay: string, days: number): string {
  return fromUtcDate(new Date(toUtcMs(isoDay) + days * MS_PER_DAY));
}

/** Giorni interi da `fromIsoDay` a `toIsoDay` (positivo se `toIsoDay` è dopo). */
export function diffIsoDays(fromIsoDay: string, toIsoDay: string): number {
  return Math.round((toUtcMs(toIsoDay) - toUtcMs(fromIsoDay)) / MS_PER_DAY);
}

/** Somma mesi di calendario; il giorno si riduce all'ultimo del mese di arrivo (31/01 + 1 = 28/02). */
export function addMonthsToIsoDay(isoDay: string, months: number): string {
  const { year, month, day } = parseIsoDay(isoDay);
  const firstOfTarget = new Date(Date.UTC(year, month - 1 + months, 1));
  const targetYear = firstOfTarget.getUTCFullYear();
  const targetMonth = firstOfTarget.getUTCMonth();
  const lastDayOfTarget = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return `${targetYear}-${pad(targetMonth + 1)}-${pad(Math.min(day, lastDayOfTarget))}`;
}

export interface RenewedDueDateInput {
  anchor: RecurrenceAnchorValue;
  issuedAt: string;
  previousDueAt: string | null;
  validityMonths: number;
}

/** SSOT del calcolo della nuova scadenza: lo usano backend (rinnovo) e anteprima frontend. */
export function computeRenewedDueDate(input: RenewedDueDateInput): string {
  const base =
    input.anchor === 'FROM_PREVIOUS_EXPIRY' && input.previousDueAt
      ? input.previousDueAt
      : input.issuedAt;
  return addMonthsToIsoDay(base, input.validityMonths);
}

export interface DeadlineBucketInput {
  dueAt: string | null;
  warningDays: readonly number[];
  takenOverUntil: string | null;
  todayIso: string;
}

/** Il bucket non si persiste mai e il frontend non lo ricalcola: arriva dal server. */
export function computeDeadlineBucket(input: DeadlineBucketInput): DeadlineBucket {
  const { dueAt, warningDays, takenOverUntil, todayIso } = input;
  if (dueAt === null) {
    return 'NO_DEADLINE';
  }
  if (takenOverUntil !== null && takenOverUntil > todayIso) {
    return 'IN_RENEWAL';
  }
  const daysLeft = diffIsoDays(todayIso, dueAt);
  if (daysLeft < 0) {
    return 'EXPIRED';
  }
  const widestWarning =
    warningDays.length > 0 ? Math.max(...warningDays) : DEFAULT_DEADLINE_WARNING_DAYS;
  return daysLeft <= widestWarning ? 'EXPIRING' : 'OK';
}

/** Un campo data in digitazione può contenere stringhe parziali: qui si distingue il giorno completo. */
export function isIsoDay(value: string): boolean {
  return ISO_DAY.test(value);
}

/**
 * Un preavviso di `warningDay` giorni cade tra oggi e la scadenza se `dueAt - n >= oggi`.
 * Senza scadenza (template, date nascoste) o con scadenza già passata non c'è un intervallo da
 * rispettare e il preavviso resta ammesso.
 */
export function isWarningDayReachable(
  warningDay: number,
  dueAtIso: string | null | undefined,
  todayIso: string,
): boolean {
  if (!dueAtIso || !isIsoDay(dueAtIso)) {
    return true;
  }
  const daysLeft = diffIsoDays(todayIso, dueAtIso);
  return daysLeft < 0 || warningDay <= daysLeft;
}

export function availableWarningDayOptions(
  options: readonly number[],
  dueAtIso: string | null | undefined,
  todayIso: string,
): number[] {
  return options.filter((warningDay) => isWarningDayReachable(warningDay, dueAtIso, todayIso));
}

export interface TakeOverUntilBounds {
  min: string;
  max: string;
}

/** Finestra ammessa per `until`: da domani a oggi + 90 giorni, estremi inclusi. */
export function getTakeOverUntilBounds(todayIso: string): TakeOverUntilBounds {
  return {
    min: addDaysToIsoDay(todayIso, 1),
    max: addDaysToIsoDay(todayIso, DEADLINE_TAKE_OVER_MAX_DAYS),
  };
}

export function isTakeOverUntilAllowed(until: string, todayIso: string): boolean {
  const { min, max } = getTakeOverUntilBounds(todayIso);
  return until >= min && until <= max;
}

/** Spec §11.1: ricorrente se e solo se la riga ha una validità. Unica fonte della regola. */
export function isRecurringDeadline(input: { validityMonths: number | null | undefined }): boolean {
  const { validityMonths } = input;
  return (
    typeof validityMonths === 'number' && Number.isInteger(validityMonths) && validityMonths > 0
  );
}

export interface AwaitingRenewalDecisionInput {
  managedBy: DeadlineManagedByValue;
  managedByUserId: string | null;
  viewerUserId: string;
  validityMonths: number | null;
  dueAt: string | null;
  takenOverUntil: string | null;
  isArchived: boolean;
  todayIso: string;
}

/**
 * Spec §11.3 "da decidere": unica del consulente che legge, scaduta, non archiviata, senza
 * presa in carico attiva. Il gemello lato query è `awaitingDecisionWhere` del repository del digest: il test di parità
 * in `consultant-deadlines-awaiting-decision-parity.component.test.ts` li tiene allineati.
 */
export function isAwaitingRenewalDecision(input: AwaitingRenewalDecisionInput): boolean {
  const isManagedByViewer =
    input.managedBy === 'CONSULTANT' &&
    input.managedByUserId !== null &&
    input.managedByUserId === input.viewerUserId;
  const isTakeOverActive = input.takenOverUntil !== null && input.takenOverUntil > input.todayIso;
  return (
    isManagedByViewer &&
    !isRecurringDeadline(input) &&
    input.dueAt !== null &&
    input.dueAt < input.todayIso &&
    !input.isArchived &&
    !isTakeOverActive
  );
}
